// §11.11 — the Recycle Bin: restore to the original path, and empty (which confirms).
//
// **Empty is the only destructive operation in the VFS** (§7.8), and the confirm that guards it is
// not drawn here — it is §19.4's shell dialog, raised inside the kernel's trash service, so this
// app and Explorer cannot end up with two different answers to "are you sure".
import { useCallback, useEffect, useState } from 'react';
import type { RecycleBinShellAccess, TrashItem } from '@hangar/shared';
import { COPY } from '@/copy';
import { formatBytes } from '@/assets/bytes';

export function RecycleBin({ access }: { access: RecycleBinShellAccess }) {
  const [items, setItems] = useState<readonly TrashItem[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void access.list().then(setItems);
  }, [access]);

  useEffect(() => {
    refresh();
    // A restore or a delete in another window changes what is in here (§18 F16 makes that two
    // tabs as well as two windows), so the list follows the tree rather than a local guess.
    return access.subscribe(refresh);
  }, [access, refresh]);

  const restore = useCallback(
    async (item: TrashItem) => {
      await access.restore(item.key);
      setMessage(COPY.recycleBin.restored(item.name));
      refresh();
    },
    [access, refresh],
  );

  const total = items?.reduce((sum, item) => sum + item.size, 0) ?? 0;

  return (
    <div className="recycle-bin" data-testid="recycle-bin">
      <div className="recycle-bar">
        <span className="recycle-usage" data-testid="recycle-usage">
          {COPY.recycleBin.usage(items?.length ?? 0, formatBytes(total))}
        </span>
        <span className="recycle-spacer" />
        <button
          type="button"
          data-testid="recycle-restore-all"
          disabled={!items || items.length === 0}
          onClick={() => {
            void (async () => {
              for (const item of items ?? []) await access.restore(item.key);
              refresh();
            })();
          }}
        >
          {COPY.recycleBin.restoreAll}
        </button>
        <button
          type="button"
          data-testid="recycle-empty"
          disabled={!items || items.length === 0}
          onClick={() => {
            // The confirm lives in the kernel (§19.4). `false` means the user said no, and the
            // list is left exactly as it was.
            void access.empty().then((emptied) => {
              if (emptied) refresh();
            });
          }}
        >
          {COPY.recycleBin.emptyBin}
        </button>
      </div>

      {items !== null && items.length === 0 ? (
        <p className="recycle-empty-state" data-testid="recycle-empty-state">
          {COPY.recycleBin.empty}
        </p>
      ) : null}

      {items !== null && items.length > 0 ? (
        <ul className="recycle-list" data-testid="recycle-list">
          {items.map((item) => (
            <li key={item.key} className="recycle-item" data-testid={`recycle-item-${item.name}`}>
              <span className="recycle-name" data-testid={`recycle-name-${item.name}`}>
                {item.name}
              </span>
              <span className="recycle-origin" title={COPY.recycleBin.originalPath}>
                {item.originalPath}
              </span>
              <span className="recycle-when">{new Date(item.deletedAt).toLocaleString()}</span>
              <button
                type="button"
                data-testid={`recycle-restore-${item.name}`}
                onClick={() => void restore(item)}
              >
                {COPY.recycleBin.restore}
              </button>
              <button
                type="button"
                data-testid={`recycle-delete-${item.name}`}
                onClick={() => {
                  void access.deleteForever(item.key).then((gone) => {
                    if (gone) refresh();
                  });
                }}
              >
                {COPY.recycleBin.deleteForever}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {message ? (
        <div className="recycle-message" role="status" data-testid="recycle-message">
          {message}
        </div>
      ) : null}
    </div>
  );
}
